import { Skeleton } from '@dnb/eufemia';
import type { ApiDto, ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import { SpectralDiagnosticsDto } from '@portals/shared/portal/ReleaseDto';
import { anyTrue } from '@portals/shared-frontend/utils';
import type { OpenAPIV3 } from 'openapi-types';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import useSWR from 'swr';

import useAuth from '@/hooks/useAuth';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import { request } from '@/request';

import { ApiProviderPageOutletContext } from '../..';
import SectionWithTitle from '../../SectionWithTitle';
import ApiStageTimeLineForm from './components/ApiStageTimeLineForm';
import { ValidateApiChampionSection } from './components/ValidateApiChampionSection';
import { ValidateSection } from './components/ValidateSection';
import { validationData } from './helpers';

type props = {
  api: ApiWithStatusAndScopesDto;
  appId: string;
};
type ApiMetadata = {
  guide: string | null;
  reference: OpenAPIV3.Document | null;
};

export default function LifecycleManagementSection({ api }: props) {
  const { isTeamAdmin, mutate } = useOutletContext<ApiProviderPageOutletContext>();
  const { roles } = useAuth();
  const isApiChampion = roles.includes('Api.Champion');
  const swrExpect404 = useSWRExpect404();

  const { data: apis, isValidating: loadingApis } = useSWR<ApiDto[]>('/api');
  const { data: apiMetadata, isValidating: loadingApiMetadata } = useSWR<ApiMetadata>(
    `/api/${api.slug}/environments/@default/releases/@latest/data`,
    swrExpect404,
  );

  const { data: spectralDiagnostics, isValidating: spectralDiagnosticsLoading } = useSWR<SpectralDiagnosticsDto>(
    `/api/${api.slug}/environments/@default/releases/@latest/spectral-diagnostics`,
    async (url) => {
      return request<SpectralDiagnosticsDto>(url, 'get');
    },
    swrExpect404,
  );

  const data = useMemo(() => {
    if (!apis) {
      return [];
    }

    return validationData(api, apis, apiMetadata?.reference, apiMetadata?.guide ?? null, spectralDiagnostics || null);
  }, [api, apiMetadata?.guide, apiMetadata?.reference, apis, spectralDiagnostics]);

  const isLoading = anyTrue(loadingApis, loadingApiMetadata, spectralDiagnosticsLoading);

  return (
    <div>
      <ApiStageTimeLineForm
        api={api}
        isApiChampion={isApiChampion}
        isTeamAdmin={isTeamAdmin}
        onStageChange={(stage) => {
          void mutate(
            {
              ...api,
              stage: stage,
            },
            false,
          );
        }}
      />
      <Skeleton show={isLoading}>
        <SectionWithTitle title="API Champions">
          <ValidateApiChampionSection />
        </SectionWithTitle>
        {data.map((item, index) => (
          <SectionWithTitle icon={null} key={item.title} title={item.title}>
            <ValidateSection
              additionalInformation={item.additionalInformation}
              data={item.inputData}
              errorKey={item.errorKey}
              errors={item.errors}
              key={index}
              title={item.title}
            />
          </SectionWithTitle>
        ))}
      </Skeleton>
    </div>
  );
}
